// src/components/courses/CourseCard.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Helper functions with i18n support
function formatDate(dateString, locale = 'en') {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatTime(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

const CourseCard = ({ course, userRole, onCourseSelect, onDeleteCourse }) => {
  const { t, i18n } = useTranslation();
  const isOwner = course.isOwner;
  
  const handleDeleteClick = () => {
    if (isOwner) {
      onDeleteCourse(course);
    }
  };

  const studentCount = course.studentCount || 0;
  const completedCount = course.completedSessionsCount || 0;
  const upcomingCount = course.upcomingSessionsCount || 0;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-slate-200 overflow-hidden">
      <div className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 mb-4 sm:mb-0">
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">{course.name}</h3>
            {course.course_code && (
              <p className="text-xs text-slate-500 mb-2">
                {t('courses.card.code')}: <span className="font-medium text-slate-600">{course.course_code}</span>
              </p>
            )}

            <div className="flex flex-wrap gap-2 sm:gap-3">
              {userRole === 'teacher' && (
                <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-50 rounded-lg">
                  <UserGroupIcon className="w-4 h-4 text-cyan-600 mr-1.5" />
                  <span className="text-xs sm:text-sm text-slate-600">
                    <span className="font-medium">{studentCount}</span> {t('courses.card.students', { count: studentCount })}
                  </span>
                </div>
              )}
              <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-50 rounded-lg">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1.5" />
                <span className="text-xs sm:text-sm text-slate-600">
                  <span className="font-medium">{completedCount}</span> {t('courses.card.completed', { count: completedCount })}
                </span>
              </div>
              <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-50 rounded-lg">
                <ClockIcon className="w-4 h-4 text-blue-500 mr-1.5" />
                <span className="text-xs sm:text-sm text-slate-600">
                  <span className="font-medium">{upcomingCount}</span> {t('courses.card.upcoming', { count: upcomingCount })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mx-6 p-3 rounded-xl border border-slate-200 bg-slate-50 text-center">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
              {t('courses.card.nextSession')}
            </div>
            {course.nextSessionStartTime ? (
              <>
                <p className="text-sm font-semibold text-slate-800">
                  {formatTime(course.nextSessionStartTime)}
                </p>
                <p className="text-xs text-slate-600">
                  {formatDate(course.nextSessionStartTime, i18n.language)}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('courses.card.noSessions')}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onCourseSelect(course.id, course.name)}
              className="flex-1 sm:flex-initial px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition-colors flex items-center justify-center"
            >
              <span className="mr-2">{t('courses.card.viewSessions')}</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
            {userRole === 'teacher' && (
              <button
                onClick={handleDeleteClick}
                disabled={!isOwner}
                className={`p-2 rounded-lg transition-colors ${
                  isOwner
                    ? 'text-red-600 hover:bg-red-50 cursor-pointer'
                    : 'text-slate-400 bg-slate-100 cursor-not-allowed'
                }`}
                title={isOwner ? t('courses.card.deleteCourse') : t('courses.card.onlyOwnerCanDelete')}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;